import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { Loading } from '../../components/Shared';

function KpiCard({ label, value, sub, color = 'var(--accent-cyan)', icon, onClick }) {
  return (
    <div className="crm-kpi-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="crm-kpi-icon" style={{ color }}>{icon}</div>
      <div className="crm-kpi-body">
        <div className="crm-kpi-value" style={{ color }}>{value ?? '—'}</div>
        <div className="crm-kpi-label">{label}</div>
        {sub && <div className="crm-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}

function PipelineBar({ etapes }) {
  const total = etapes.reduce((s, e) => s + parseInt(e.nb_opps || 0), 0);
  if (total === 0) return <div className="crm-pipeline-empty">Aucune opportunité en cours</div>;
  return (
    <div className="crm-pipeline-bars">
      {etapes.filter(e => !e.etape_finale).map(e => {
        const nb = parseInt(e.nb_opps || 0);
        const pct = total > 0 ? Math.round((nb / total) * 100) : 0;
        const montant = parseFloat(e.montant_total || 0);
        return (
          <div key={e.libelle} className="crm-pipeline-bar-row">
            <div className="crm-pipeline-bar-label">
              <span className="crm-pipeline-dot" style={{ background: e.couleur || '#5c6078' }} />
              {e.libelle}
            </div>
            <div className="crm-pipeline-bar-track">
              <div className="crm-pipeline-bar-fill"
                style={{ width: `${pct}%`, background: e.couleur || '#5c6078' }} />
            </div>
            <div className="crm-pipeline-bar-meta">
              <span className="crm-pipeline-nb">{nb}</span>
              {montant > 0 && (
                <span className="crm-pipeline-montant">
                  {montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CRMDashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/crm/dashboard')
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (error) return <div className="crm-error">Erreur : {error}</div>;
  if (!data) return null;

  const fmt = n => Number(n || 0).toLocaleString('fr-FR');
  const fmtEur = n => Number(n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

  return (
    <div className="crm-dashboard">

      {/* KPIs */}
      <div className="crm-kpi-grid">
        <KpiCard
          label="Prospects actifs"
          value={fmt(data.prospects)}
          color="var(--accent-cyan)"
          icon="🎯"
          onClick={() => onNavigate('clients')}
        />
        <KpiCard
          label="Opportunités en cours"
          value={fmt(data.opportunites_en_cours)}
          sub={data.opportunites_montant > 0 ? fmtEur(data.opportunites_montant) : null}
          color="var(--accent-purple)"
          icon="📈"
          onClick={() => onNavigate('opportunites')}
        />
        <KpiCard
          label="CA pondéré (pipeline)"
          value={fmtEur(data.ca_pondere)}
          sub="Σ montant × probabilité"
          color="#34d399"
          icon="💶"
        />
        <KpiCard
          label="Interactions (7 jours)"
          value={fmt(data.interactions_7j)}
          color="var(--accent-amber)"
          icon="💬"
          onClick={() => onNavigate('interactions')}
        />
        <KpiCard
          label="Clients à relancer"
          value={fmt(data.clients_a_relancer)}
          sub="Date dépassée"
          color={data.clients_a_relancer > 0 ? '#f87171' : '#34d399'}
          icon="🔔"
          onClick={() => onNavigate('clients')}
        />
        <KpiCard
          label="Rappels recyclage (30 j)"
          value={fmt(data.rappels_proches)}
          sub="Échéance proche"
          color={data.rappels_proches > 0 ? '#f59e0b' : '#34d399'}
          icon="♻️"
          onClick={() => onNavigate('rappels')}
        />
      </div>

      {/* Conversion */}
      {(data.taux_conversion_30j?.nouveaux > 0) && (
        <div className="crm-section">
          <div className="crm-section-title">Conversion — 30 derniers jours</div>
          <div className="crm-conversion-row">
            <span>{data.taux_conversion_30j.nouveaux} nouveau{data.taux_conversion_30j.nouveaux > 1 ? 'x' : ''} prospect{data.taux_conversion_30j.nouveaux > 1 ? 's' : ''}</span>
            <span className="crm-arrow">→</span>
            <span style={{ color: '#34d399', fontWeight: 700 }}>
              {data.taux_conversion_30j.convertis} converti{data.taux_conversion_30j.convertis > 1 ? 's' : ''}
            </span>
            {data.taux_conversion_30j.nouveaux > 0 && (
              <span className="crm-taux-badge">
                {Math.round((data.taux_conversion_30j.convertis / data.taux_conversion_30j.nouveaux) * 100)}%
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pipeline par étape */}
      {data.pipeline?.length > 0 && (
        <div className="crm-section">
          <div className="crm-section-title">Pipeline commercial</div>
          <PipelineBar etapes={data.pipeline} />
          <button className="crm-link-btn" onClick={() => onNavigate('opportunites')}>
            Voir toutes les opportunités →
          </button>
        </div>
      )}
    </div>
  );
}
